import { deleteRequest, getRequest, postRequest } from "api/axiosClient";
import { BaseParamsRequest } from "api/request/basParams.request";
import {
  GetFlightDetailRequestParams,
  GetAirlineDetailRequestParams,
  ConvertCurrencyRequestParams,
  GetFreightModesRequestParams,
  GetFlightDataRequestParams,
  SetStatesRequestParams,
  GetTimezonesRequestParams,
  GetPaymentLocationRequestParams,
  GetCitiesListRequestParams,
  GetCountriesRequestParams,
  GetHolidaysRequestParams,
  GetPostalCodeGeocodeRequestParams,
} from "../request";
import {
  ConvertCurrencyResponseType,
  GetAirlinesResponseType,
  GetAirportsResponseType,
  GetCitiesResponseType,
  GetCountriesResponseType,
  GetCurrenciesResponseType,
  GetFeatureFlagsResponseType,
  GetFileResponseType,
  GetFlightDataResponseType,
  GetFreightModesResponseType,
  GetHolidaysResponseType,
  GetLocodesResponseType,
  GetPaymentLocationResponseType,
  GetPostalCodeGeocodeResponseType,
  GetScacCodesResponseType,
  GetSeaportsResponseType,
  GetStatesResponseType,
  GetStatesV2ResponseType,
  GetTimezonesResponseType,
  RequestFormStartingPointResponseType,
  UploadFileResponseType,
} from "../response";

const { REACT_APP_CARGOBASE_BASE_URL } = import.meta.env;
const baseURL = `${REACT_APP_CARGOBASE_BASE_URL}/common`;

const deleteFile = (reference: string | undefined) => deleteRequest(`${baseURL}/files/${reference}`);
const getFile = (reference: string) => getRequest<GetFileResponseType>(`${baseURL}/files/${reference}`);
const getAirports = (query: string) => getRequest<GetAirportsResponseType>(`${baseURL}/airport?q=${query}`);
const getAirlines = (query: string) => getRequest<GetAirlinesResponseType>(`${baseURL}/airlines?name=${query}`);
const getCountries = (config: BaseParamsRequest<GetCountriesRequestParams> = {}) =>
  getRequest<GetCountriesResponseType>(`${baseURL}/countries`, config);
const getCurrencies = () => getRequest<GetCurrenciesResponseType>(`${baseURL}/currencies`);
const getFeatureFlags = () => getRequest<GetFeatureFlagsResponseType>(`${baseURL}/feature-flags`);

const getFreightModes = (config: BaseParamsRequest<GetFreightModesRequestParams> = {}, includeCustom: boolean = true) =>
  getRequest<GetFreightModesResponseType>(`${baseURL}/freight-modes?include_custom_freight_modes=${includeCustom}`, config);
const getFlightData = (config: BaseParamsRequest<GetFlightDataRequestParams> = {}) =>
  getRequest<GetFlightDataResponseType>(`${baseURL}/flight-data`, config);

const getScacCodes = () => getRequest<GetScacCodesResponseType>(`${baseURL}/supported-scac`);
const getSeaports = (query: string) => getRequest<GetSeaportsResponseType>(`${baseURL}/seaport?q=${query}`);
const getStates = (config: BaseParamsRequest<SetStatesRequestParams> = {}) =>
  getRequest<GetStatesResponseType>(`${baseURL}/states`, config);
const getTimezones = (config: BaseParamsRequest<GetTimezonesRequestParams> = {}) =>
  getRequest<GetTimezonesResponseType>(`${baseURL}/timezones`, config);

const uploadFile = (payload: Object, config: BaseParamsRequest<Object> = {}) =>
  postRequest<UploadFileResponseType>(`${baseURL}/files`, payload, config);
const getAirlineDetail = (query: string, config: BaseParamsRequest<GetAirlineDetailRequestParams> = {}) =>
  getRequest<GetAirlinesResponseType>(`${baseURL}/airlines?q=${query}`, config);
const getFlightDetail = (config: BaseParamsRequest<GetFlightDetailRequestParams> = {}) =>
  getRequest<GetFlightDataResponseType>(`${baseURL}/flight-data`, config);
const convertCurrency = (config: BaseParamsRequest<ConvertCurrencyRequestParams> = {}) =>
  getRequest<ConvertCurrencyResponseType>(`${baseURL}/currency-conversion`, config);

const requestFormStartingPoint = () =>
  getRequest<RequestFormStartingPointResponseType>(`${REACT_APP_CARGOBASE_BASE_URL}/starting-point`);
const getPaymentLocationFormDetails = (
  location: string,
  config: BaseParamsRequest<GetPaymentLocationRequestParams> = {},
  isQuote: boolean = false,
) =>
  getRequest<GetPaymentLocationResponseType>(
    `${REACT_APP_CARGOBASE_BASE_URL}${!isQuote ? "/quick-request" : ""}/location/${location}/details`,
    config,
  );
const getAttachmentBuckets = (paymentLocation: string, config = {}) =>
  getRequest(`${REACT_APP_CARGOBASE_BASE_URL}/attachment-bucket?payment_location=${paymentLocation}`, config);

const getCities = (config: BaseParamsRequest<GetCitiesListRequestParams>) =>
  getRequest<GetCitiesResponseType>(`${baseURL}/cities`, config);
const getStatesV2 = (countryId: number) => getRequest<GetStatesV2ResponseType>(`${baseURL}/states-v2?country=${countryId}`);
const getLocodes = (countryId: number) => getRequest<GetLocodesResponseType>(`${baseURL}/locodes?country=${countryId}`);
const getHolidays = (config: BaseParamsRequest<GetHolidaysRequestParams>) =>
  getRequest<GetHolidaysResponseType>(`${baseURL}/holidays`, config);
const getPostalCodeGeocode = (config: BaseParamsRequest<GetPostalCodeGeocodeRequestParams>) =>
  getRequest<GetPostalCodeGeocodeResponseType>(`${baseURL}/geocode/postal-code`, config);

export {
  deleteFile,
  getFile,
  getAirports,
  getAirlines,
  getCountries,
  getCurrencies,
  getFeatureFlags,
  getFreightModes,
  getFlightData,
  getScacCodes,
  getStates,
  getSeaports,
  getTimezones,
  uploadFile,
  convertCurrency,
  requestFormStartingPoint,
  getPaymentLocationFormDetails,
  getFlightDetail,
  getAirlineDetail,
  getAttachmentBuckets,
  getCities,
  getStatesV2,
  getLocodes,
  getHolidays,
  getPostalCodeGeocode,
};
